import {createSlice} from '@reduxjs/toolkit'
import geoBoundaryConfig from '../../appConfig/geographicBoundaryConfig.json'
import distributionDimensionConfig from '../../appConfig/populationDistributionDimension.config.json'
import {configs, getConfigData} from '../../shared/helpers/configHelper'

const getInitialState = () => ({
  sdohConfig: {},
  POIConfig: {},
  miscConfig: {},
  healthAndEquityConfig: {},
  statesConfig: {},
  defaultPOIConfig: {},
  dataAggregationConfig: {},
  personasConfig: {},
  filterConfig: {}, // Unified filter configuration
  useCasesConfig: {},
  geoBoundaryConfig,
  distributionDimensionConfig,
  isLoading: false,
  isInitialized: false
})

const configSlice = createSlice({
  name: 'config',
  initialState: getInitialState(),
  reducers: {
    setConfig: (state, action) => {
      Object.assign(state, action.payload)
    },
    setPOIConfig: (state, action) => {
      state.POIConfig = action.payload
    },
    setMiscConfig: (state, action) => {
      state.miscConfig = action.payload
    },
    setStatesConfig: (state, action) => {
      state.statesConfig = action.payload
    },
    setDefaultPOIConfig: (state, action) => {
      state.defaultPOIConfig = action.payload
    },
    setDataAggregationConfig: (state, action) => {
      state.dataAggregationConfig = action.payload
    },
    setPersonasConfig: (state, action) => {
      state.personasConfig = action.payload
    },
    setFilterConfig: (state, action) => {
      state.filterConfig = action.payload
    },
    setUseCasesConfig: (state, action) => {
      state.useCasesConfig = action.payload
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setIsInitialized: (state, action) => {
      state.isInitialized = action.payload
    }
  }
})

// Export actions
export const {
  setConfig,
  setPOIConfig,
  setMiscConfig,
  setStatesConfig,
  setDefaultPOIConfig,
  setDataAggregationConfig,
  setPersonasConfig,
  setFilterConfig,
  setUseCasesConfig,
  setIsLoading,
  setIsInitialized
} = configSlice.actions

// Thunk action to load all config data
export const loadAllConfigs = () => async (dispatch, getState) => {
  // Prevent duplicate loads - check if already loading or initialized
  const state = getState()
  if (state.config.isLoading || state.config.isInitialized) {
    return
  }
  dispatch(setIsLoading(true))
  try {
    const [
      poiConfigData,
      miscConfigData,
      defaultStateConfigData,
      defaultPOIConfigData,
      dataAggregationConfigData,
      personasConfigData,
      filterConfigData,
      useCasesConfigData
    ] = await Promise.all([
      getConfigData(configs.POI),
      getConfigData(configs.MISC),
      getConfigData(configs.DEFAULT_STATE),
      getConfigData(configs.DEFAULT_POI),
      getConfigData(configs.DATA_AGGREGATION),
      getConfigData(configs.PERSONAS),
      getConfigData(configs.FILTERS),
      getConfigData(configs.USECASE)
    ])

    dispatch(
      setConfig({
        POIConfig: poiConfigData,
        miscConfig: miscConfigData,
        statesConfig: defaultStateConfigData,
        defaultPOIConfig: defaultPOIConfigData,
        dataAggregationConfig: dataAggregationConfigData,
        personasConfig: personasConfigData,
        filterConfig: filterConfigData,
        useCasesConfig: useCasesConfigData,
        isInitialized: true,
        isLoading: false
      })
    )
  } catch (error) {
    console.error('Error loading configs:', error)
    dispatch(setIsLoading(false))
  }
}

// Export selectors
export const selectConfig = (state) => state.config
export const selectPOIConfig = (state) => state.config.POIConfig
export const selectMiscConfig = (state) => state.config.miscConfig
export const selectStatesConfig = (state) => state.config.statesConfig
export const selectDefaultPOIConfig = (state) => state.config.defaultPOIConfig
export const selectDataAggregationConfig = (state) => state.config.dataAggregationConfig
export const selectPersonasConfig = (state) => state.config.personasConfig
export const selectFilterConfig = (state) => state.config.filterConfig
export const selectUseCasesConfig = (state) => state.config.useCasesConfig
export const selectGeoBoundaryConfig = (state) => state.config.geoBoundaryConfig
export const selectDistributionDimensionConfig = (state) => state.config.distributionDimensionConfig
export const selectIsConfigLoading = (state) => state.config.isLoading
export const selectIsConfigInitialized = (state) => state.config.isInitialized

// Export reducer as default
export default configSlice.reducer
